import { useState } from 'react'
import {
  Spinner,
  StatTrendChart,
  formatPctTrend,
  type MetricKey,
  type StatWindow,
} from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { useAgentStats, type StatsScope } from '../_lib/agent-api'

// 数据统计页(规格 §8)：5 KPI 数字卡 + 共享 StatTrendChart 多指标趋势。
// agent 看自己归因(self;可切 self/subtree)。无 Top 排行(admin 专属)。

const WINDOW_DAYS: Record<StatWindow, number> = { '7d': 7, '30d': 30, '90d': 90 }

// KPI 卡片配置：今日访问 IP / 今日登录用户 / 今日新注册 / 总存量用户 / 今日新增佣金
const KPI_CARDS: Array<{ key: MetricKey; label: string; unit?: string }> = [
  { key: 'visit_ip', label: '今日访问 IP' },
  { key: 'login_user', label: '今日登录用户' },
  { key: 'new_user', label: '今日新注册' },
  { key: 'total_user', label: '总存量用户' },
  { key: 'commission_coin', label: '今日新增佣金', unit: 'COIN' },
]

export function AgentStatsPage() {
  const [chartWindow, setChartWindow] = useState<StatWindow>('30d')
  const [metric, setMetric] = useState<MetricKey>('visit_ip')
  const [scope, setScope] = useState<StatsScope>('self')

  const { data, isLoading } = useAgentStats(WINDOW_DAYS[chartWindow], scope)

  const series = data?.series ?? []
  const today = data?.today
  // 较昨日 = series 末尾(今日)与倒数第二点对比;不足 2 点 → prev undefined → 显示「—」
  const prev = series.length >= 2 ? series[series.length - 2] : undefined

  return (
    <AgentLayout topbarTitle="数据统计">
      <div data-testid="agent-stats-page" className="flex flex-col gap-5 p-6">
        {/* 归因范围切换(MVP：self / subtree) */}
        <div className="flex items-center justify-end">
          <ScopeSwitch value={scope} onChange={setScope} />
        </div>

        {/* 5 个 KPI 数字卡 */}
        <section data-testid="agent-stats-kpis" className="grid grid-cols-5 gap-4">
          {isLoading ? (
            <div className="col-span-5 flex justify-center py-6">
              <Spinner className="text-amber-500" />
            </div>
          ) : (
            KPI_CARDS.map((c) => (
              <KpiCard
                key={c.key}
                testid={`agent-stats-kpi-${c.key}`}
                label={c.label}
                value={(today?.[c.key] ?? 0).toLocaleString()}
                todayVal={today?.[c.key] ?? 0}
                prevVal={prev?.[c.key]}
                {...(c.unit ? { unit: c.unit } : {})}
              />
            ))
          )}
        </section>

        {/* 多指标趋势图 */}
        <StatTrendChart
          series={series}
          metric={metric}
          onMetricChange={setMetric}
          window={chartWindow}
          onWindowChange={setChartWindow}
          accent="#F59E0B"
        />
      </div>
    </AgentLayout>
  )
}

function KpiCard({
  testid,
  label,
  value,
  unit,
  todayVal,
  prevVal,
}: {
  testid: string
  label: string
  value: string
  unit?: string
  todayVal: number
  prevVal: number | undefined
}) {
  const { dir, text } = formatPctTrend(todayVal, prevVal)
  const cls = dir === 'up' ? 'text-emerald-500' : dir === 'down' ? 'text-red-500' : 'text-gray-400'
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white px-5 py-4"
    >
      <p className="text-[13px] text-gray-500">{label}</p>
      <p className="flex items-baseline gap-1.5">
        <span className="text-[28px] font-bold tabular-nums text-gray-900">{value}</span>
        {unit && <span className="text-sm font-medium text-amber-500">{unit}</span>}
      </p>
      <p className={`text-xs ${cls}`}>{text}</p>
    </div>
  )
}

function ScopeSwitch({
  value,
  onChange,
}: {
  value: StatsScope
  onChange: (v: StatsScope) => void
}) {
  const options: Array<{ key: StatsScope; label: string }> = [
    { key: 'self', label: '仅本人' },
    { key: 'subtree', label: '含下级' },
  ]
  return (
    <div
      data-testid="agent-stats-scope"
      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5"
      role="tablist"
    >
      {options.map((o) => {
        const active = o.key === value
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            data-testid={`agent-stats-scope-${o.key}`}
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={[
              'rounded px-3 py-1 text-[12px] font-medium transition-colors',
              active ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100',
            ].join(' ')}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
